import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import {
  Button,
  CircularProgress,
  Paper,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core'
import { Delete as DeleteIcon, FileCopy as FileCopyIcon } from '@material-ui/icons'
import moment from 'moment'
import NewProjectModal from './NewProjectModal'
import ScopeSelectionModal from './ScopeSelectionModal'

const statusChipProps = (status) => {
  switch (status) {
    case 'draft':
      return { label: 'Draft', color: 'default', variant: 'default' }
    case 'estimating':
      return { label: 'Estimating', color: 'primary', variant: 'default' }
    case 'review':
      return { label: 'Review', color: 'secondary', variant: 'default' }
    case 'submitted':
      return { label: 'Submitted', color: 'primary', variant: 'outlined' }
    case 'won':
      return { label: 'Won', style: { backgroundColor: '#4caf50', color: '#fff' } }
    case 'lost':
      return { label: 'Lost', style: { backgroundColor: '#f44336', color: '#fff' } }
    default:
      return { label: status || 'Draft', color: 'default', variant: 'default' }
  }
}

const ProjectList = (props) => {
  const history = useHistory()
  const [projects, setProjects] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [modalOpen, setModalOpen] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState(null)
  const [scopeModalProject, setScopeModalProject] = React.useState(null)
  const [cloning, setCloning] = React.useState(null)

  const handleCloneClick = (e, project) => {
    e.stopPropagation()
    setCloning(project._id)
    axios.post(`${props.url}/api/bid-estimation/projects/${project._id}/clone`).then(res => {
      setCloning(null)
      history.push(`/bid-estimation/${res.data._id}`)
    }).catch(err => {
      setCloning(null)
      console.error('Clone failed:', err)
    })
  }

  React.useEffect(() => {
    fetchProjects()
  }, [])

  const fetchProjects = () => {
    setLoading(true)
    axios.get(`${props.url}/api/bid-estimation/projects`).then(response => {
      setProjects(response.data)
      setLoading(false)
    }).catch(err => {
      console.error(err)
      setLoading(false)
    })
  }

  const handleRowClick = (projectId) => {
    history.push(`/bid-estimation/${projectId}`)
  }

  const handleDeleteClick = (e, project) => {
    e.stopPropagation()
    setDeleteTarget(project)
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    axios.delete(`${props.url}/api/bid-estimation/projects/${deleteTarget._id}`).then(() => {
      setDeleteTarget(null)
      fetchProjects()
    }).catch(err => {
      console.error(err)
      setDeleteTarget(null)
    })
  }

  const handleSave = (createdProject) => {
    setModalOpen(false)
    fetchProjects()
    if (createdProject) {
      setScopeModalProject(createdProject)
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
        <Grid item>
          <Typography variant="h5">Bid Estimation</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={() => setModalOpen(true)}>
            New Project
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Buildings</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" style={{ padding: 20 }}>
                      No projects yet. Click "New Project" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                projects.map(project => {
                  const chipProps = statusChipProps(project.status)
                  return (
                    <TableRow
                      key={project._id}
                      hover
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(project._id)}
                    >
                      <TableCell>{project.name}</TableCell>
                      <TableCell>{project.client || '—'}</TableCell>
                      <TableCell>{(project.projectType || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={chipProps.label}
                          color={chipProps.color || undefined}
                          variant={chipProps.variant || 'default'}
                          style={chipProps.style || {}}
                        />
                      </TableCell>
                      <TableCell>{project.bidDueDate ? moment(project.bidDueDate).format('MM/DD/YYYY') : '—'}</TableCell>
                      <TableCell>{project.teamMode ? 'Team' : 'Solo'}</TableCell>
                      <TableCell>{project.buildings ? project.buildings.length : 0}</TableCell>
                      <TableCell>
                        {project.updatedAt ? moment(project.updatedAt).format('MM/DD/YYYY h:mm a') : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleCloneClick(e, project)}
                          disabled={!!cloning}
                          title="Clone project"
                        >
                          {cloning === project._id ? <CircularProgress size={16} /> : <FileCopyIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, project)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <NewProjectModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        url={props.url}
      />

      <ScopeSelectionModal
        open={!!scopeModalProject}
        project={scopeModalProject}
        onComplete={(projectId) => {
          setScopeModalProject(null)
          history.push(`/bid-estimation/${projectId}`)
        }}
        onSkip={(projectId) => {
          setScopeModalProject(null)
          if (projectId) history.push(`/bid-estimation/${projectId}`)
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ProjectList)
